export const MANDATORY_IMPORT_FIELDS = [
  { key: 'firstName', label: 'Nombre (firstName)' },
  { key: 'lastName', label: 'Apellido (lastName)' },
  { key: 'email', label: 'E-Mail (email)' },
];

export const OPTIONAL_IMPORT_FIELDS = [
  { key: 'dni', label: 'D.N.I. (dni)' },
  { key: 'phone', label: 'Teléfono (phone)' },
];

const HEADER_ALIASES = {
  firstName: [
    'firstname',
    'first_name',
    'first name',
    'name',
    'nombre',
    'nombres',
    'primer nombre',
  ],
  lastName: [
    'lastname',
    'last_name',
    'last name',
    'surname',
    'apellido',
    'apellidos',
  ],
  email: [
    'email',
    'e-mail',
    'mail',
    'correo',
    'correo electronico',
    'correo electrónico',
    'email address',
  ],
  dni: [
    'dni',
    'd.n.i.',
    'd.n.i',
    'documento',
    'nro documento',
    'numero de documento',
    'número de documento',
    'doc',
  ],
  phone: [
    'phone',
    'telefono',
    'teléfono',
    'celular',
    'tel',
    'movil',
    'móvil',
    'whatsapp',
    'phone number',
  ],
};

/**
 * Formats a person's first or last name so every word starts with an uppercase letter and remaining letters are lowercase.
 * @param {string} rawName raw name string from CSV
 * @returns {string} capitalized name string
 */
export function capitalizePersonName(rawName) {
  if (!rawName || typeof rawName !== 'string') {
    return '';
  }
  return rawName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function detectDelimiter(sampleLine) {
  const delimiters = [',', ';', '\t'];
  let bestDelimiter = ',';
  let maxCount = -1;

  delimiters.forEach((delimiter) => {
    let count = 0;
    let insideQuotes = false;
    for (let charIndex = 0; charIndex < sampleLine.length; charIndex += 1) {
      const currentChar = sampleLine[charIndex];
      if (currentChar === '"') {
        insideQuotes = !insideQuotes;
      } else if (!insideQuotes && currentChar === delimiter) {
        count += 1;
      }
    }
    if (count > maxCount) {
      maxCount = count;
      bestDelimiter = delimiter;
    }
  });

  return bestDelimiter;
}

/**
 * Parses raw CSV content into a two-dimensional array of string cells, handling BOM, quoted values, and delimiter detection.
 * @param {string} rawCsvText raw text content read from a CSV file
 * @returns {Array<Array<string>>} non-empty parsed rows of cells
 */
export function parseCsvGrid(rawCsvText) {
  if (!rawCsvText || typeof rawCsvText !== 'string') {
    return [];
  }

  const normalizedText = rawCsvText.replace(/^\uFEFF/, '');
  const firstLineBreak = normalizedText.search(/\r?\n/);
  const firstLine =
    firstLineBreak === -1
      ? normalizedText
      : normalizedText.slice(0, firstLineBreak);
  const delimiter = detectDelimiter(firstLine);

  const rows = [];
  let currentRow = [];
  let currentCell = '';
  let insideQuotes = false;

  for (let charIndex = 0; charIndex < normalizedText.length; charIndex += 1) {
    const currentChar = normalizedText[charIndex];
    const nextChar = normalizedText[charIndex + 1];

    if (currentChar === '"') {
      if (insideQuotes && nextChar === '"') {
        currentCell += '"';
        charIndex += 1;
      } else {
        insideQuotes = !insideQuotes;
      }
    } else if (!insideQuotes && currentChar === delimiter) {
      currentRow.push(currentCell.trim());
      currentCell = '';
    } else if (
      !insideQuotes &&
      (currentChar === '\n' || currentChar === '\r')
    ) {
      if (currentChar === '\r' && nextChar === '\n') {
        charIndex += 1;
      }
      currentRow.push(currentCell.trim());
      if (currentRow.some((cell) => cell !== '')) {
        rows.push(currentRow);
      }
      currentRow = [];
      currentCell = '';
    } else {
      currentCell += currentChar;
    }
  }

  currentRow.push(currentCell.trim());
  if (currentRow.some((cell) => cell !== '')) {
    rows.push(currentRow);
  }

  return rows;
}

export function extractCsvStructure(grid, hasHeaderRow = true) {
  if (!Array.isArray(grid) || grid.length === 0) {
    return { columns: [], dataRows: [] };
  }

  const maxColumns = grid.reduce(
    (maxCount, row) => Math.max(maxCount, row.length),
    0
  );

  const headerSource = hasHeaderRow ? grid[0] : [];
  const columns = [];
  for (let columnIndex = 0; columnIndex < maxColumns; columnIndex += 1) {
    const rawHeader = headerSource[columnIndex];
    const label =
      hasHeaderRow && rawHeader
        ? rawHeader
        : `Columna ${columnIndex + 1}`;
    columns.push({
      index: String(columnIndex),
      label,
    });
  }

  const dataRows = hasHeaderRow ? grid.slice(1) : grid;
  return { columns, dataRows };
}

export function autoDetectFieldMapping(columns) {
  const initialMapping = {
    firstName: '',
    lastName: '',
    email: '',
    dni: '',
    phone: '',
  };

  if (!Array.isArray(columns)) {
    return initialMapping;
  }

  const usedIndices = new Set();

  Object.keys(HEADER_ALIASES).forEach((fieldKey) => {
    const aliases = HEADER_ALIASES[fieldKey];
    const matchedColumn = columns.find((column) => {
      if (usedIndices.has(column.index)) {
        return false;
      }
      const normalizedLabel = column.label.trim().toLowerCase();
      return aliases.includes(normalizedLabel);
    });

    if (matchedColumn) {
      initialMapping[fieldKey] = matchedColumn.index;
      usedIndices.add(matchedColumn.index);
    }
  });

  return initialMapping;
}

export function mapCsvRowToStudent(row, fieldMapping, rowNumber) {
  const getCell = (columnKey) => {
    const columnIndex = fieldMapping[columnKey];
    if (columnIndex === '' || columnIndex === undefined || columnIndex === null) {
      return '';
    }
    const numericIndex = Number(columnIndex);
    return row[numericIndex] !== undefined ? String(row[numericIndex]).trim() : '';
  };

  const rawFirstName = getCell('firstName');
  const rawLastName = getCell('lastName');
  const rawEmail = getCell('email');
  const rawDni = getCell('dni');
  const rawPhone = getCell('phone');

  const firstName = capitalizePersonName(rawFirstName);
  const lastName = capitalizePersonName(rawLastName);
  const email = rawEmail.toLowerCase();
  const dni = rawDni;
  const phone = rawPhone;

  const missingFields = [];
  if (!firstName) {
    missingFields.push('Nombre');
  }
  if (!lastName) {
    missingFields.push('Apellido');
  }
  if (!email) {
    missingFields.push('E-Mail');
  }

  return {
    rowNumber,
    firstName,
    lastName,
    email,
    dni,
    phone,
    isValid: missingFields.length === 0,
    missingFields,
  };
}

/**
 * Builds the request payload for POST /api/students combining row data with the shared course card configuration.
 * @param {object} mappedStudent mapped and formatted student fields from CSV
 * @param {object} courseConfig shared commission, plan, and course state
 * @returns {object} student creation payload
 */
export function buildStudentImportPayload(mappedStudent, courseConfig) {
  const filteredCourses = (courseConfig.course || []).filter(Boolean);
  const filteredClassrooms = (courseConfig.classroom || []).filter(Boolean);
  const filteredPrices = (courseConfig.price || [])
    .filter((priceItem) => priceItem !== '' && priceItem !== null && priceItem !== undefined)
    .map((priceItem) => Number(priceItem));
  const filteredInstallments = (courseConfig.monthlyInstallments || [])
    .filter((installmentItem) => installmentItem !== '' && installmentItem !== null && installmentItem !== undefined)
    .map((installmentItem) => Number(installmentItem));
  const filteredActiveStates = (courseConfig.isActive || []).filter(Boolean);
  const filteredPlans = (courseConfig.plan || []).filter(Boolean);
  const scholarshipList =
    Array.isArray(courseConfig.scholarship) && courseConfig.scholarship.length > 0
      ? courseConfig.scholarship.map((item) => Boolean(item))
      : [false];

  return {
    additionalNotes: courseConfig.additionalNotes || '',
    address: '',
    availableSat: false,
    availableTimeFrom: '',
    availableTimeTo: '',
    birthDate: '',
    classroom: filteredClassrooms,
    classroomYear: Number(courseConfig.classroomYear) || new Date().getFullYear(),
    codeKnowleadge: 'low',
    commissionChangedReason: courseConfig.commissionChangedReason || ['', '', ''],
    contactOrigin: '',
    country: courseConfig.country || 'Argentina',
    course: filteredCourses,
    courseQuantity: courseConfig.courseQuantity || 1,
    currency: courseConfig.currency || ['ARS', '', ''],
    currentJob: '',
    dni: mappedStudent.dni || '',
    downReason: courseConfig.downReason || ['', '', ''],
    email: (mappedStudent.email || '').toLowerCase(),
    englishClassroom: '',
    englishKnowleadge: 'low',
    fatherLastName: '',
    fatherName: '',
    informativeTalkDate: '',
    isActive: filteredActiveStates,
    isRollingKid: false,
    itKnowleadge: 'low',
    location: '',
    moduleCommissionChangedReason:
      courseConfig.moduleCommissionChangedReason || ['', '', ''],
    moduleDownReason: courseConfig.moduleDownReason || ['', '', ''],
    monthlyInstallments: filteredInstallments,
    name: capitalizePersonName(mappedStudent.firstName),
    notebookModel: '',
    otherCommissionChangedReason:
      courseConfig.otherCommissionChangedReason || ['', '', ''],
    otherDownReason: courseConfig.otherDownReason || ['', '', ''],
    ownNotebook: false,
    phone: mappedStudent.phone || '',
    phoneAlt: '',
    plan: filteredPlans,
    preferredSite: 'S.M.T.',
    price: filteredPrices,
    region: courseConfig.region || 'Tucumán',
    scholarship: scholarshipList,
    surname: capitalizePersonName(mappedStudent.lastName),
    temporalInstallmentQuantity:
      courseConfig.temporalInstallmentQuantity !== '' &&
      courseConfig.temporalInstallmentQuantity !== null &&
      courseConfig.temporalInstallmentQuantity !== undefined
        ? Number(courseConfig.temporalInstallmentQuantity)
        : filteredInstallments[0] || 1,
  };
}
